import React, { useEffect, useRef } from "react";
import { ALERT_ZONE_ORDER, alertZone, depotInTransit, driverAlertRows } from "./monitorAlerts";
import { DriverOpenOptions, inactivityOpenOptions, noDeliveriesLabel } from "./driverFocus";
import { formatMinutes } from "./towerFormat";
import { activateOnKey } from "./towerShared";
import type { TowerDriverDetail, TowerListedShipment } from "../../types/controlTower";

const LEVEL_LABELS = { critical: "Crítico", warning: "Leve" } as const;

// Toda la fila abre lo que la alerta señala; también con Enter o espacio.
function rowProps(open: () => void) {
  return {
    role: "button",
    tabIndex: 0,
    onClick: open,
    onKeyDown: activateOnKey(open),
  };
}

// Cuántas alertas hay hoy: una por chofer más una por el depósito.
export function towerAlertCount(
  drivers: TowerDriverDetail[],
  depot: TowerListedShipment[],
  today: string,
  nowMs: number,
) {
  return (
    driverAlertRows(drivers, nowMs, "today", today).length + (depotInTransit(depot, "today", today).length ? 1 : 0)
  );
}

// Panel de alertas del día: se abre encima de la Torre desde la campanita,
// como el detalle de un chofer, sin desplazar las zonas.
export default function TowerAlertsPanel({
  drivers,
  depot,
  today,
  nowMs,
  onClose,
  onOpenDriver,
  onOpenDepot,
}: {
  drivers: TowerDriverDetail[];
  depot: TowerListedShipment[];
  today: string;
  nowMs: number;
  onClose: () => void;
  onOpenDriver: (driverId: string, options?: DriverOpenOptions) => void;
  onOpenDepot: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => {
      if (dialog?.open) dialog.close();
    };
  }, []);
  const rows = driverAlertRows(drivers, nowMs, "today", today);
  const depotStuck = depotInTransit(depot, "today", today);
  const total = rows.length + (depotStuck.length ? 1 : 0);
  const flex = depotStuck.filter((s) => s.shippingUniverse === "FLEX").length;
  const nonFlex = depotStuck.filter((s) => s.shippingUniverse === "NON_FLEX").length;
  // Una sección por zona, en el orden de Monitoreo; dentro, el orden de gravedad.
  const groups = [...ALERT_ZONE_ORDER, "Sin zona"]
    .map((zone) => ({ zone, items: rows.filter((row) => alertZone(row.driver, "today", today) === zone) }))
    .filter((group) => group.items.length > 0);

  return (
    <dialog
      ref={ref}
      className="tower-dialog tower-alerts-panel"
      aria-labelledby="tower-alerts-title"
      onCancel={onClose}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="tower-alerts-head">
        <h2 id="tower-alerts-title">
          Alertas <span className={`tower-tab-badge${total ? "" : " empty"}`}>{total}</span>
        </h2>
        <button type="button" className="tower-alerts-close" onClick={onClose} aria-label="Cerrar alertas">
          ×
        </button>
      </div>
      {total === 0 && <p className="tower-muted tower-alert-empty">Sin alertas por ahora.</p>}
      <div className="tower-alerts-list">
        {depotStuck.length > 0 && (
          <section className="tower-alerts-group" aria-label="Depósito">
            <h3>
              Depósito <span>1</span>
            </h3>
            <ul>
              <li {...rowProps(onOpenDepot)} aria-label="Ver los envíos del depósito que quedaron En camino">
                <span className="tower-alert-level review">Revisar</span>
                <div className="tower-alert-main">
                  <strong>Quedaron En camino</strong>
                  <small>
                    {depotStuck.length} envío{depotStuck.length === 1 ? "" : "s"} · {flex} Flex · {nonFlex} particulares
                  </small>
                </div>
              </li>
            </ul>
          </section>
        )}
        {groups.map(({ zone, items }) => (
          <section key={zone} className="tower-alerts-group" aria-label={`Zona ${zone}`}>
            <h3>
              {zone} <span>{items.length}</span>
            </h3>
            <ul>
              {items.map(({ driver, alert, flexInTransit, localities }) => {
                const label =
                  alert.level === "noDeliveries" ? noDeliveriesLabel(alert.movedToday) : LEVEL_LABELS[alert.level];
                const open = () =>
                  onOpenDriver(driver.id, inactivityOpenOptions(alert, flexInTransit, driver.lastDelivery));
                // La zona ya es el encabezado: solo se nombran las otras en las que opera.
                const otherZones = driver.zones.filter((z) => z !== "Sin clasificar" && z !== zone);
                return (
                  <li key={driver.id} {...rowProps(open)} aria-label={`${label}: abrir el detalle de ${driver.name}`}>
                    <span className={`tower-alert-level ${alert.level}`}>{label}</span>
                    <div className="tower-alert-main">
                      <strong>{driver.name}</strong>
                      <small>
                        {[
                          otherZones.length ? `También ${otherZones.join(" / ")}` : "",
                          ...localities.map((l) => `${l.name} (${l.count})`),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </small>
                    </div>
                    <div className="tower-alert-facts">
                      <span>
                        <b>{flexInTransit}</b> Flex
                      </span>
                      <span>
                        <b>{"idleMinutes" in alert ? formatMinutes(alert.idleMinutes) : "—"}</b>{" "}
                        {"idleMinutes" in alert
                          ? "sin entregar"
                          : alert.movedToday
                            ? "con movimiento"
                            : "sin movimiento"}
                      </span>
                      <span>Última {driver.lastDelivery?.slice(11) || "—"}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </dialog>
  );
}
